window.ProductAddonsCore = (function () {
  'use strict';

  var registry = new Map(); // form element -> { providers: [], priceProviders: [], basePrice: null }

  function register(rootEl, providerFn, priceProviderFn) {
    var form = rootEl.closest('form[data-product-form]') || rootEl.closest('form[action*="/cart/add"]');
    if (!form) {
      console.error('ProductAddonsCore: no enclosing product form found for', rootEl);
      return;
    }

    var entry = registry.get(form);
    if (!entry) {
      entry = { providers: [], priceProviders: [], basePrice: null };
      registry.set(form, entry);
      attachFormHandler(form, entry);
    }
    entry.providers.push(providerFn);
    if (typeof priceProviderFn === 'function') {
      entry.priceProviders.push(priceProviderFn);
    }

    updatePriceDisplay(form, entry);
  }

  // Called by an addon widget after any selection change, so the
  // displayed "configured price" stays in sync as the customer clicks
  // around. Safe to call often — this only reads current state.
  function notifyChange(rootEl) {
    var form = rootEl.closest('form[data-product-form]') || rootEl.closest('form[action*="/cart/add"]');
    if (!form) return;
    var entry = registry.get(form);
    if (!entry) return;
    updatePriceDisplay(form, entry);
  }

  function attachFormHandler(form, entry) {
    var submitBtn = form.querySelector('[data-add-to-cart]');
    if (!submitBtn) {
      console.error('ProductAddonsCore: no add-to-cart button (data-add-to-cart) found on form', form);
      return;
    }

    function handleActivation(e) {
      // Canceling the button's click here (rather than listening for the
      // form's `submit` event) prevents the theme's own submit-driven
      // ProductForm handler from ever running — see county-selector.js's
      // original integration note for why racing `submit` listeners on
      // the same element is unreliable.
      safePreventDefault(e);
      safeStopPropagation(e);
      runSubmission(form, submitBtn, entry);
    }

    submitBtn.addEventListener('click', handleActivation, true);
    form.addEventListener('submit', handleActivation, true); // backstop: implicit submission (e.g. Enter key)

    // Recalculate the displayed price whenever the main quantity changes,
    // since every addon's price contribution scales with it too.
    var qtyInput = form.querySelector('[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener('input', function () { updatePriceDisplay(form, entry); });
      qtyInput.addEventListener('change', function () { updatePriceDisplay(form, entry); });
    }
  }

  function runSubmission(form, submitBtn, entry) {
    var addonItems = [];
    var mainItemProperties = {};

    for (var i = 0; i < entry.providers.length; i++) {
      var result;
      try {
        result = entry.providers[i]();
      } catch (err) {
        console.error('ProductAddonsCore: a provider threw an error', err);
        return;
      }
      if (!result || result.ok === false) {
        return; // provider already surfaced its own error UI
      }
      if (result.items && result.items.length) {
        addonItems = addonItems.concat(result.items);
      }
      // A provider can optionally contribute properties directly onto
      // the MAIN product's cart line instead of (or alongside) adding
      // its own separate addon items — for a widget that's purely
      // informational and shouldn't create any separate priced line at
      // all (e.g. a "which upgrade keys are already in your scanner?"
      // note), this is the only contribution it makes.
      if (result.mainItemProperties) {
        mainItemProperties = mergeProps(mainItemProperties, result.mainItemProperties);
      }
    }

    submitCart(form, submitBtn, addonItems, mainItemProperties);
  }

  function submitCart(form, submitBtn, addonItems, mainItemProperties) {
    var mainIdInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var mainVariantId = mainIdInput ? parseInt(mainIdInput.value, 10) : null;
    var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    // Some products exist purely as a carrier for one of these widgets
    // (e.g. the standalone Programming Key Upgrade product) — the
    // page's own "main" variant isn't a real, separate purchase on top
    // of whatever the widget itself resolves to, so adding it as its
    // own cart line would double up whatever the customer actually
    // selected. product-form.liquid sets this flag on the form itself
    // for exactly those product types.
    var suppressMainItem = form.dataset.suppressMainItem === 'true';

    if (!suppressMainItem && !mainVariantId) {
      console.error('ProductAddonsCore: could not determine the main variant id (input[name="id"])');
      return;
    }

    // Every item from this single add-to-cart action gets a shared,
    // unique group ID — this is what lets order processing (e.g. a
    // NetSuite sync) reliably match a county/memory/antenna/etc. addon
    // line back to the EXACT scanner it was purchased alongside, even
    // in the edge case where a customer buys the same scanner model
    // twice in one order with different addon selections each time.
    var groupId = generateGroupId();
    var productLabel = form.dataset.productTitle || form.dataset.productHandle || '';

    // Every addon's quantity scales with the main product's quantity —
    // buying N scanners means each one needs its own copy of every
    // selected addon (N sets of counties programmed, N memory upgrades,
    // N programming keys, etc.), not just one shared set.
    var taggedAddonItems = addonItems.map(function (item) {
      var props = mergeProps(item.properties, { 'Order Group': groupId });
      if (productLabel) props['Program For'] = productLabel;
      return { id: item.id, quantity: item.quantity * quantity, properties: props };
    });

    var items = taggedAddonItems.slice();
    if (!suppressMainItem) {
      items.unshift({
        id: mainVariantId,
        quantity: quantity,
        properties: mergeProps(mainItemProperties, { 'Order Group': groupId })
      });
    }

    if (items.length === 0) {
      // Nothing to add at all (main line suppressed and nothing was
      // selected in the widget) — surface this instead of silently
      // hitting cart/add.js with an empty items array.
      window.alert('Please make a selection before adding this to your cart.');
      return;
    }

    setLoading(submitBtn, true);

    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw err; });
        return res.json();
      })
      .then(function () {
        // Theme-agnostic fallback. Swap for an in-page cart-drawer
        // refresh if Impulse's theme.js exposes a public hook for one.
        window.location.href = rootUrl + 'cart';
      })
      .catch(function (err) {
        setLoading(submitBtn, false);
        console.error('ProductAddonsCore: /cart/add.js failed', err);
        window.alert((err && err.description) || 'Something went wrong adding this to your cart.');
      });
  }

  // --- Live "configured price" display ---
  // Sums the base product price plus every registered addon's currently
  // selected price contribution, multiplies by the main quantity (since
  // every addon scales with it too — see submitCart above), and writes
  // the result into the theme's own price element ([data-price]).
  function updatePriceDisplay(form, entry) {
    var priceEls = document.querySelectorAll('[data-price]');
    if (!priceEls.length) return;

    if (entry.basePrice === null) {
      entry.basePrice = parsePrice(priceEls[0].textContent);
    }

    var qtyInput = form.querySelector('[name="quantity"]');
    var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var suppressMainItem = form.dataset.suppressMainItem === 'true';

    var addonUnitTotal = 0;
    entry.priceProviders.forEach(function (fn) {
      var value = 0;
      try {
        value = fn();
      } catch (err) {
        console.error('ProductAddonsCore: a price provider threw an error', err);
      }
      addonUnitTotal += (typeof value === 'number' && !isNaN(value)) ? value : 0;
    });

    // On a suppress-main-item product (see submitCart), the page's own
    // displayed price is an arbitrary artifact of whichever variant
    // happens to be "current" — it isn't a real base price the widget's
    // selections are added on top of, so it's excluded here entirely.
    var effectiveBase = suppressMainItem ? 0 : entry.basePrice;

    var total = (effectiveBase + addonUnitTotal) * quantity;
    var formatted = formatMoney(total);
    priceEls.forEach(function (el) { el.textContent = formatted; });
  }

  function parsePrice(text) {
    var num = parseFloat(String(text).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? 0 : num;
  }

  function formatMoney(amount) {
    return '$' + amount.toFixed(2);
  }
  // --- End live price display ---

  function generateGroupId() {
    // Fixed-length, uppercase, easy to scan/type in a report. Excludes
    // visually ambiguous characters (0/O, 1/I/L) since a human will be
    // reading and possibly typing this to match lines across an order.
    var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var id = '';
    for (var i = 0; i < 8; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  }

  function mergeProps(base, extra) {
    var result = {};
    var key;
    if (base) {
      for (key in base) {
        if (Object.prototype.hasOwnProperty.call(base, key)) result[key] = base[key];
      }
    }
    for (key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) result[key] = extra[key];
    }
    return result;
  }

  function setLoading(submitBtn, isLoading) {
    submitBtn.disabled = isLoading;
    submitBtn.classList.toggle('btn--loading', isLoading);
  }

  function safePreventDefault(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
  }

  function safeStopPropagation(e) {
    if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    else if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
  }

  return { register: register, notifyChange: notifyChange };
})();